import React, { useState } from "react";
import "./AddMatch.css";

import { auth, db } from "../firebase";

import {
  collection,
  addDoc,
  getDocs,
  query,
  where,
  serverTimestamp,
} from "firebase/firestore";

/* =========================================================
   CONFIG
========================================================= */

const TIMEZONE = "Africa/Dar_es_Salaam";

/* =========================================================
   DEFAULT CORRECT SCORES
========================================================= */

const defaultScoreOdds = [
  { score: "0 - 0", odds: 1.45 },
  { score: "1 - 0", odds: 4.85 },
  { score: "0 - 1", odds: 5.26 },
  { score: "1 - 1", odds: 1.87 },
  { score: "2 - 1", odds: 3.17 },
  { score: "1 - 2", odds: 3.08 },
  { score: "0 - 2", odds: 4.18 },
  { score: "2 - 0", odds: 2.25 },
  { score: "2 - 2", odds: 1.95 },
  { score: "0 - 3", odds: 2.92 },
  { score: "3 - 0", odds: 3.78 },
  { score: "3 - 3", odds: 1.1 },
  { score: "2 - 3", odds: 1.63 },
  { score: "3 - 2", odds: 1.05 },
  { score: "1 - 3", odds: 3.38 },
  { score: "3 - 1", odds: 2.43 },
];

/* =========================================================
   TODAY
========================================================= */

const getToday = () => {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
};

/* =========================================================
   SAFE NUMBER
========================================================= */

const toSafeNumber = (value, fallback = 0) => {
  const number = Number(value);

  return Number.isFinite(number)
    ? number
    : fallback;
};

/* =========================================================
   DATE VALIDATION
========================================================= */

const isValidDateString = (date) => {
  if (
    typeof date !== "string" ||
    !/^\d{4}-\d{2}-\d{2}$/.test(date)
  ) {
    return false;
  }

  const parsed = new Date(
    `${date}T00:00:00Z`
  );

  return (
    !Number.isNaN(parsed.getTime()) &&
    parsed.toISOString().slice(0, 10) === date
  );
};

/* =========================================================
   TIME VALIDATION
========================================================= */

const normaliseTime = (value) => {
  if (!value) return "";

  const match = String(value)
    .trim()
    .match(/^(\d{1,2}):(\d{2})/);

  if (!match) return "";

  const hours = Number(match[1]);
  const minutes = Number(match[2]);

  if (
    hours < 0 ||
    hours > 23 ||
    minutes < 0 ||
    minutes > 59
  ) {
    return "";
  }

  return `${String(hours).padStart(2, "0")}:${String(
    minutes
  ).padStart(2, "0")}`;
};

/* =========================================================
   COMPONENT
========================================================= */

export default function AddMatch() {
  const [manualData, setManualData] = useState("");

  const [manualUploading, setManualUploading] =
    useState(false);

  const [successMessage, setSuccessMessage] =
    useState("");

  const [errorMessage, setErrorMessage] =
    useState("");

  /* =========================================================
     GET CORRECT SCORES
  ========================================================= */

  const getCorrectScores = (match) => {
    if (
      Array.isArray(match?.correctScores) &&
      match.correctScores.length > 0
    ) {
      return match.correctScores.map((item) => ({
        score: String(
          item?.score || ""
        ).trim(),

        odds: toSafeNumber(
          item?.odds,
          0
        ),
      }));
    }

    return defaultScoreOdds.map((item) => ({
      ...item,
    }));
  };

  /* =========================================================
     CHECK DUPLICATE
  ========================================================= */

  const isDuplicate = async (match) => {
    if (!match) {
      return false;
    }

    /*
     * Check by sofaId only when manually supplied.
     */
    if (
      match.sofaId !== null &&
      match.sofaId !== undefined &&
      String(match.sofaId).trim() !== ""
    ) {
      const sofaQuery = query(
        collection(db, "matches"),
        where(
          "sofaId",
          "==",
          match.sofaId
        )
      );

      const sofaSnapshot =
        await getDocs(sofaQuery);

      if (!sofaSnapshot.empty) {
        return true;
      }
    }

    /*
     * Main duplicate check:
     * Home team + Away team + Date
     */
    if (!match.A) {
      return false;
    }

    const fallbackQuery = query(
      collection(db, "matches"),
      where(
        "A",
        "==",
        match.A
      )
    );

    const snapshot =
      await getDocs(fallbackQuery);

    return snapshot.docs.some((doc) => {
      const data = doc.data();

      return (
        String(
          data?.B || ""
        ).trim() ===
          String(
            match?.B || ""
          ).trim() &&
        String(
          data?.date || ""
        ).trim() ===
          String(
            match?.date || ""
          ).trim()
      );
    });
  };

  /* =========================================================
     CLEAN JSON
  ========================================================= */

  const cleanJsonText = (text) => {
    let clean = String(
      text || ""
    ).trim();

    /*
     * Remove markdown code fence.
     *
     * Supports:
     * ```json
     * {...}
     * ```
     */
    clean = clean.replace(
      /^```(?:json)?\s*/i,
      ""
    );

    clean = clean.replace(
      /\s*```$/i,
      ""
    );

    clean = clean.trim();

    /*
     * Remove trailing commas.
     */
    clean = clean.replace(
      /,\s*([}\]])/g,
      "$1"
    );

    return clean;
  };

  /* =========================================================
     PARSE MANUAL MATCHES
  ========================================================= */

  const parseManualMatches = (text) => {
    const clean =
      cleanJsonText(text);

    if (!clean) {
      throw new Error(
        "Hakuna manual data."
      );
    }

    try {
      const parsed =
        JSON.parse(clean);

      if (Array.isArray(parsed)) {
        return parsed;
      }

      return [parsed];
    } catch (error) {
      console.error(
        "Manual JSON parse error:",
        error
      );

      throw new Error(
        "Format ya manual matches sio sahihi.\n\n" +
          "Tumia JSON sahihi yenye quotes."
      );
    }
  };

  /* =========================================================
     NORMALISE MANUAL MATCH
  ========================================================= */

  const normaliseManualMatch = (item) => {
    if (
      !item ||
      typeof item !== "object" ||
      Array.isArray(item)
    ) {
      throw new Error(
        "Manual match si object sahihi."
      );
    }

    if (!item.A) {
      throw new Error(
        "Kila match lazima iwe na A."
      );
    }

    if (!item.B) {
      throw new Error(
        "Kila match lazima iwe na B."
      );
    }

    if (!item.date) {
      throw new Error(
        `${item.A} vs ${item.B} haina date.`
      );
    }

    const rawDate = String(
      item.date
    ).trim();

    /*
     * Accept:
     * YYYY-MM-DD
     * YYYY-MM-DD HH:mm
     * YYYY-MM-DD HH:mm:ss
     * YYYY-MM-DDTHH:mm:ss
     */
    const dateMatch =
      rawDate.match(
        /^(\d{4}-\d{2}-\d{2})/
      );

    if (!dateMatch) {
      throw new Error(
        `${item.A} vs ${item.B}: date sio sahihi. ` +
          "Tumia YYYY-MM-DD au YYYY-MM-DD HH:mm:ss."
      );
    }

    const cleanDate =
      dateMatch[1];

    if (
      !isValidDateString(
        cleanDate
      )
    ) {
      throw new Error(
        `${item.A} vs ${item.B}: invalid date ${cleanDate}.`
      );
    }

    /*
     * TIME
     */
    let matchTime = item.time
      ? String(item.time).trim()
      : "";

    /*
     * If time is inside date:
     * 2026-08-28 22:00:00
     */
    if (!matchTime) {
      const found =
        rawDate.match(
          /(?:T|\s)(\d{1,2}):(\d{2})/
        );

      if (found) {
        matchTime =
          `${found[1]}:${found[2]}`;
      }
    }

    matchTime =
      normaliseTime(matchTime);

    /*
     * TIMESTAMP
     */
    let timestamp =
      item.startTimestamp ??
      null;

    /*
     * For manually entered match,
     * create browser timestamp if possible.
     */
    if (
      timestamp === null ||
      timestamp === undefined ||
      timestamp === ""
    ) {
      if (matchTime) {
        const localDate =
          new Date(
            `${cleanDate}T${matchTime}:00`
          );

        if (
          !Number.isNaN(
            localDate.getTime()
          )
        ) {
          timestamp =
            Math.floor(
              localDate.getTime() /
                1000
            );
        }
      }
    }

    return {
      /*
       * TEAMS
       */
      A: String(
        item.A
      ).trim(),

      B: String(
        item.B
      ).trim(),

      /*
       * DATE / TIME
       */
      date: cleanDate,

      time: matchTime,

      /*
       * LEAGUE
       */
      league: String(
        item.league ||
          "League"
      ).trim(),

      /*
       * COUNTRY
       */
      country: String(
        item.country ||
          "Other"
      ).trim(),

      /*
       * LOGOS
       */
      logoA: String(
        item.logoA ||
          ""
      ).trim(),

      logoB: String(
        item.logoB ||
          ""
      ).trim(),

      /*
       * SOURCE
       */
      source: "manual",

      /*
       * OPTIONAL ID
       */
      sofaId:
        item.sofaId ??
        null,

      /*
       * TIMESTAMP
       */
      startTimestamp:
        timestamp,

      /*
       * TIMEZONE
       */
      timezone: String(
        item.timezone ||
          TIMEZONE
      ).trim(),

      /*
       * STATUS
       */
      status: String(
        item.status ||
          ""
      ).trim(),

      statusDescription:
        String(
          item.statusDescription ||
            ""
        ).trim(),

      /*
       * OPTIONAL TEAM IDs
       */
      homeTeamId:
        item.homeTeamId ??
        null,

      awayTeamId:
        item.awayTeamId ??
        null,

      tournamentId:
        item.tournamentId ??
        null,

      /*
       * CORRECT SCORES
       */
      correctScores:
        getCorrectScores(item),
    };
  };

  /* =========================================================
     SAVE MANUAL MATCHES
  ========================================================= */

  const saveManualMatches = async () => {
    setSuccessMessage("");
    setErrorMessage("");

    if (!manualData.trim()) {
      setErrorMessage(
        "Paste match kwanza."
      );

      return;
    }

    if (manualUploading) {
      return;
    }

    if (!auth.currentUser) {
      setErrorMessage(
        "Huja-login. Tafadhali login kwanza."
      );

      return;
    }

    try {
      setManualUploading(true);

      const parsed =
        parseManualMatches(
          manualData
        );

      if (parsed.length === 0) {
        throw new Error(
          "Hakuna matches kwenye data."
        );
      }

      const manualMatches =
        parsed.map(
          normaliseManualMatch
        );

      let added = 0;
      let skipped = 0;
      let failed = 0;

      for (
        const match of manualMatches
      ) {
        try {
          const duplicate =
            await isDuplicate(
              match
            );

          if (duplicate) {
            skipped++;
            continue;
          }

          await addDoc(
            collection(
              db,
              "matches"
            ),
            {
              ...match,

              /*
               * Always manual.
               */
              source: "manual",

              /*
               * Firebase server timestamp.
               */
              createdAt:
                serverTimestamp(),
            }
          );

          added++;
        } catch (error) {
          console.error(
            "Manual upload failed:",
            match,
            error
          );

          failed++;
        }
      }

      /*
       * SUCCESS
       */
      setSuccessMessage(
        `${added} manual match${
          added === 1
            ? ""
            : "es"
        } zimehifadhiwa Firebase. ` +
          `${skipped} duplicate zimerukwa. ` +
          `${failed} zimeshindwa.`
      );

      if (added > 0) {
        setManualData("");
      }
    } catch (error) {
      console.error(
        "Manual save error:",
        error
      );

      setErrorMessage(
        error?.message ||
          "Unknown error."
      );
    } finally {
      setManualUploading(false);
    }
  };

  /* =========================================================
     CLEAR
  ========================================================= */

  const clearManualData = () => {
    setManualData("");
    setSuccessMessage("");
    setErrorMessage("");
  };

  /* =========================================================
     RENDER
  ========================================================= */

  return (
    <div className="container">

      {/* =====================================================
          HEADER
      ===================================================== */}

      <div className="page-header">

        <h2 className="title">
          ⚽ Smart Admin Matches
        </h2>

        <p className="timezone">
          East Africa Time (EAT)
        </p>

      </div>

      {/* =====================================================
          MANUAL MATCH
      ===================================================== */}

      <section className="manual-section">

        <div className="section-header">

          <h3>
            ✍️ Manual Match
          </h3>

          <span className="section-badge">
            MANUAL
          </span>

        </div>

        <p className="section-description">
          Ingiza match moja au matches
          nyingi kwa JSON. Correct scores
          na odds zitaongezwa automatically.
        </p>

        {/* =================================================
            SUCCESS
        ================================================= */}

        {successMessage && (
          <div
            className="manual-success"
            role="alert"
          >
            ✅ {successMessage}
          </div>
        )}

        {/* =================================================
            ERROR
        ================================================= */}

        {errorMessage && (
          <div
            className="manual-error"
            role="alert"
          >
            ❌ {errorMessage}
          </div>
        )}

        {/* =================================================
            TEXTAREA
        ================================================= */}

        <textarea
          className="manual-textarea"
          value={manualData}
          onChange={(event) => {
            setManualData(
              event.target.value
            );

            setSuccessMessage("");
            setErrorMessage("");
          }}
          placeholder={`{
  "A": "Barcelona",
  "B": "Real Madrid",
  "date": "2026-08-28",
  "time": "22:00",
  "league": "LaLiga",
  "country": "Spain",
  "logoA": "",
  "logoB": "",
  "source": "manual",
  "sofaId": null,
  "timezone": "Africa/Dar_es_Salaam"
}`}
        />

        {/* =================================================
            BUTTONS
        ================================================= */}

        <div
          className="manual-actions"
          style={{
            display: "flex",
            gap: "10px",
            flexWrap: "wrap",
          }}
        >

          <button
            type="button"
            className="manual-save-btn"
            onClick={
              saveManualMatches
            }
            disabled={
              manualUploading ||
              !manualData.trim()
            }
          >
            {manualUploading
              ? "⏳ Saving..."
              : "💾 Save Manual Matches"}
          </button>

          <button
            type="button"
            className="clear-button"
            onClick={
              clearManualData
            }
            disabled={
              manualUploading ||
              !manualData.trim()
            }
          >
            Clear
          </button>

        </div>

      </section>

      {/* =====================================================
          INFORMATION
      ===================================================== */}

      <section
        className="manual-section"
        style={{
          marginTop: "20px",
        }}
      >

        <div className="section-header">

          <h3>
            ℹ️ Match Entry
          </h3>

          <span className="section-badge">
            FIREBASE
          </span>

        </div>

        <p className="section-description">
          Matches zinaingizwa manually na
          kuhifadhiwa moja kwa moja kwenye
          Firebase collection{" "}
          <strong>matches</strong>.
        </p>

        <div
          style={{
            display: "grid",
            gap: "10px",
            marginTop: "15px",
          }}
        >

          <div>
            <strong>A</strong> — Home team
          </div>

          <div>
            <strong>B</strong> — Away team
          </div>

          <div>
            <strong>date</strong> — YYYY-MM-DD
          </div>

          <div>
            <strong>time</strong> — HH:mm
          </div>

          <div>
            <strong>league</strong> — League name
          </div>

          <div>
            <strong>country</strong> — Country
          </div>

          <div>
            <strong>logoA / logoB</strong> —
            Optional logo URLs
          </div>

        </div>

      </section>

      {/* =====================================================
          EMPTY STATE
      ===================================================== */}

      <div className="empty-state">

        <div className="empty-icon">
          ⚽
        </div>

        <h3>
          Ready for Manual Match
        </h3>

        <p>
          Ingiza match kwenye sehemu ya
          Manual Match hapo juu.
        </p>

      </div>

    </div>
  );
}